import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import BackofficeLayout, { Card, Btn, fmtEUR, fmtPct } from "./BackofficeLayout";
import { api } from "../../lib/api";
import { toast, Toaster } from "sonner";
import { Plus, HardHat, ArrowRight, Trash2, Edit } from "lucide-react";

const STATUS_LABEL = { etude: "Étude", en_cours: "En cours", livre: "Livré", gele: "Gelé" };
const STATUS_TONE = {
  etude: "bg-sky-500/10 text-sky-300 border-sky-500/30",
  en_cours: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  livre: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  gele: "bg-slate-500/10 text-slate-400 border-slate-500/30",
};

const empty = {
  name: "", entity_id: "", budget_total: 0, financement_bancaire_total: 0, apport_initial: 0,
  start_date: "", expected_end_date: "", status: "etude",
  surface_m2: "", location: "", notes: "", expected_monthly_rent: 0, property_value: "",
};

export default function ProjectsAdmin() {
  const [items, setItems] = useState([]);
  const [entities, setEntities] = useState([]);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = () =>
    Promise.all([
      api.get("/backoffice/projects").then((r) => r.data),
      api.get("/backoffice/entities").then((r) => r.data),
    ]).then(([p, e]) => { setItems(p); setEntities(e); });
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ ...empty, entity_id: entities[0]?.id || "" }); setShowForm(true); };

  const startEdit = (p) => {
    setEditing(p.id);
    setForm({
      name: p.name, entity_id: p.entity_id,
      budget_total: p.budget_total || 0, financement_bancaire_total: p.financement_bancaire_total || 0,
      apport_initial: p.apport_initial || 0,
      start_date: p.start_date || "", expected_end_date: p.expected_end_date || "",
      status: p.status, surface_m2: p.surface_m2 || "",
      location: p.location || "", notes: p.notes || "",
      expected_monthly_rent: p.expected_monthly_rent || 0,
      property_value: p.property_value ?? "",
    });
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      budget_total: parseFloat(form.budget_total) || 0,
      financement_bancaire_total: parseFloat(form.financement_bancaire_total) || 0,
      apport_initial: parseFloat(form.apport_initial) || 0,
      surface_m2: form.surface_m2 === "" ? null : parseFloat(form.surface_m2),
      expected_monthly_rent: parseFloat(form.expected_monthly_rent) || 0,
      property_value: form.property_value === "" ? null : (parseFloat(form.property_value) || null),
      start_date: form.start_date || null,
      expected_end_date: form.expected_end_date || null,
    };
    try {
      if (editing) {
        await api.put(`/backoffice/projects/${editing}`, payload);
        toast.success("Projet mis à jour");
      } else {
        await api.post("/backoffice/projects", payload);
        toast.success("Projet créé");
      }
      setShowForm(false); setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce projet ? Les dépenses associées seront supprimées, les prêts détachés.")) return;
    await api.delete(`/backoffice/projects/${id}`);
    toast.success("Supprimé");
    load();
  };

  return (
    <BackofficeLayout
      title="Projets & Travaux"
      subtitle="Suivi des opérations immobilières : budget, dépenses, financement, avancement."
      actions={<Btn onClick={openNew} data-testid="bof-project-new-btn"><Plus className="w-3.5 h-3.5" /> Nouveau projet</Btn>}
    >
      <Toaster theme="dark" position="top-right" />

      {showForm && (
        <Card className="mb-6" data-testid="bof-project-form">
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <F label="Nom" span={2}>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                     placeholder="Site 2 — 2520 m²" className="input" data-testid="bof-project-name" />
            </F>
            <F label="Entité">
              <select required value={form.entity_id} onChange={(e) => setForm({ ...form, entity_id: e.target.value })} className="input" data-testid="bof-project-entity">
                <option value="">— Choisir —</option>
                {entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
            </F>
            <F label="Statut">
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="input" data-testid="bof-project-status">
                {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </F>
            <F label="Surface (m²)">
              <input type="number" value={form.surface_m2} onChange={(e) => setForm({ ...form, surface_m2: e.target.value })} className="input" />
            </F>
            <F label="Localisation">
              <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="input" placeholder="Cercottes RN20" />
            </F>
            <F label="Budget total (€)">
              <input type="number" step="0.01" required value={form.budget_total} onChange={(e) => setForm({ ...form, budget_total: e.target.value })} className="input" data-testid="bof-project-budget" />
            </F>
            <F label="Financement bancaire (€)">
              <input type="number" step="0.01" value={form.financement_bancaire_total} onChange={(e) => setForm({ ...form, financement_bancaire_total: e.target.value })} className="input" />
            </F>
            <F label="Apport initial (€)">
              <input type="number" step="0.01" value={form.apport_initial} onChange={(e) => setForm({ ...form, apport_initial: e.target.value })} className="input" />
            </F>
            <F label="Loyer futur estimé (€/mois)">
              <input type="number" step="0.01" value={form.expected_monthly_rent} onChange={(e) => setForm({ ...form, expected_monthly_rent: e.target.value })} className="input" />
            </F>
            <F label="Valeur du bien estimée (€)">
              <input type="number" step="0.01" value={form.property_value}
                     onChange={(e) => setForm({ ...form, property_value: e.target.value })}
                     placeholder="Ex : 250000 (utilisé pour LTV)"
                     className="input" data-testid="bof-project-property-value" />
              <div className="text-[10px] text-slate-500 mt-1">
                Sert au calcul de la LTV. Si vide, le budget total est utilisé comme approximation.
              </div>
            </F>
            <F label="Début">
              <input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="input" />
            </F>
            <F label="Livraison prévue">
              <input type="date" value={form.expected_end_date} onChange={(e) => setForm({ ...form, expected_end_date: e.target.value })} className="input" />
            </F>
            <F label="Notes" span={3}>
              <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input" />
            </F>
            <div className="md:col-span-3 flex gap-2">
              <Btn type="submit" data-testid="bof-project-save">{editing ? "Enregistrer" : "Créer"}</Btn>
              <Btn type="button" variant="ghost" onClick={() => setShowForm(false)}>Annuler</Btn>
            </div>
          </form>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="bof-projects-list">
        {items.length === 0 && (
          <div className="lg:col-span-2 text-center text-slate-500 py-12">Aucun projet. Créez votre premier projet immobilier.</div>
        )}
        {items.map((p) => (
          <Card key={p.id} data-testid={`bof-project-${p.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-2 mb-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <HardHat className="w-4 h-4 text-amber-400" />
                  <span className={`text-[10px] px-2 py-0.5 rounded border font-semibold uppercase ${STATUS_TONE[p.status] || STATUS_TONE.etude}`}>
                    {STATUS_LABEL[p.status] || p.status}
                  </span>
                </div>
                <div className="font-bold text-white text-lg">{p.name}</div>
                <div className="text-xs text-slate-500">{p.entity_name} · {p.location || "—"} {p.surface_m2 ? `· ${p.surface_m2} m²` : ""}</div>
              </div>
              <div className="flex gap-1 shrink-0">
                <Btn variant="ghost" onClick={() => startEdit(p)} data-testid={`bof-project-edit-${p.id}`}>
                  <Edit className="w-3.5 h-3.5" />
                </Btn>
                <Btn variant="danger" onClick={() => remove(p.id)} data-testid={`bof-project-delete-${p.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Btn>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Budget</div>
                <div className="font-bold text-white">{fmtEUR(p.budget_total)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Dépenses</div>
                <div className="font-bold text-white">{fmtEUR(p.kpi?.depenses_totales)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Financement</div>
                <div className="font-bold text-white">{fmtEUR(p.kpi?.capital_bancaire_total)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Débloqué</div>
                <div className="font-bold text-white">{fmtEUR(p.kpi?.capital_debloque)}</div>
              </div>
            </div>

            <div className="mb-3">
              <div className="flex justify-between items-baseline mb-1">
                <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Avancement financier</span>
                <span className="text-xs font-bold text-amber-300">{fmtPct(p.kpi?.avancement_financier)}</span>
              </div>
              <div className="h-1.5 bg-slate-800 rounded overflow-hidden">
                <div className="h-full bg-gradient-to-r from-amber-500 to-amber-300"
                     style={{ width: `${Math.min(100, (p.kpi?.avancement_financier || 0) * 100)}%` }} />
              </div>
            </div>

            <div className="mb-4">
              <div className="flex justify-between items-baseline mb-1">
                <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Usage dette</span>
                <span className="text-xs font-bold text-sky-300">{fmtPct(p.kpi?.debt_usage_ratio)}</span>
              </div>
              <div className="h-1.5 bg-slate-800 rounded overflow-hidden">
                <div className="h-full bg-gradient-to-r from-sky-500 to-sky-300"
                     style={{ width: `${Math.min(100, (p.kpi?.debt_usage_ratio || 0) * 100)}%` }} />
              </div>
            </div>

            {/* ==== Ratios bancaires LTV / DSCR ==== */}
            {(p.kpi?.loans_count || 0) > 0 && (
              <div className="mb-4 p-3 rounded border border-slate-700 bg-slate-900/40" data-testid={`bof-project-ratios-${p.id}`}>
                <div className="text-[10px] uppercase tracking-widest text-amber-400 font-bold mb-2">Ratios bancaires</div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1">
                      LTV
                      <span className="text-slate-600 normal-case tracking-normal text-[9px]">(loan-to-value)</span>
                    </div>
                    <div className={`font-bold ${
                      p.kpi?.ltv_pct == null ? "text-slate-500"
                      : p.kpi.ltv_pct < 60 ? "text-emerald-300"
                      : p.kpi.ltv_pct < 80 ? "text-amber-300"
                      : "text-red-300"
                    }`}>
                      {p.kpi?.ltv_pct != null ? `${p.kpi.ltv_pct.toFixed(1)} %` : "—"}
                    </div>
                    <div className="text-[9px] text-slate-500 mt-0.5">
                      {p.kpi?.ltv_basis === "property_value" ? "vs valeur du bien" : p.kpi?.ltv_basis === "budget" ? "vs budget (défaut)" : "renseigne valeur"}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1">
                      DSCR
                      <span className="text-slate-600 normal-case tracking-normal text-[9px]">(couverture dette)</span>
                    </div>
                    <div className={`font-bold ${
                      p.kpi?.dscr == null ? "text-slate-500"
                      : p.kpi.dscr >= 1.25 ? "text-emerald-300"
                      : p.kpi.dscr >= 1.0 ? "text-amber-300"
                      : "text-red-300"
                    }`}>
                      {p.kpi?.dscr != null ? p.kpi.dscr.toFixed(2) : "—"}
                    </div>
                    <div className="text-[9px] text-slate-500 mt-0.5">
                      loyer / mensualité annuels
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-slate-800 text-[10px] text-slate-500">
                  <div>
                    <div className="uppercase tracking-widest text-slate-500 font-semibold">CRD actuel</div>
                    <div className="font-mono text-amber-300 font-bold">{fmtEUR(p.kpi?.crd_total)}</div>
                  </div>
                  <div>
                    <div className="uppercase tracking-widest text-slate-500 font-semibold">Revenu annuel</div>
                    <div className="font-mono text-emerald-300 font-bold">{fmtEUR(p.kpi?.annual_revenue)}</div>
                  </div>
                  <div>
                    <div className="uppercase tracking-widest text-slate-500 font-semibold">Service dette /an</div>
                    <div className="font-mono text-red-300 font-bold">{fmtEUR(p.kpi?.annual_debt_service)}</div>
                  </div>
                </div>
              </div>
            )}

            <Link to={`/backoffice-finance/projets/${p.id}`}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-300 hover:text-amber-200"
                  data-testid={`bof-project-detail-${p.id}`}>
              Voir détail <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </Card>
        ))}
      </div>

      <style>{`
        .input { background: rgba(15, 23, 42, 0.6); border: 1px solid rgb(51, 65, 85); color: white;
                 padding: 0.5rem 0.75rem; border-radius: 0.375rem; width: 100%; font-size: 0.875rem; }
        .input:focus { outline: 2px solid rgb(251, 191, 36); outline-offset: -1px; }
      `}</style>
    </BackofficeLayout>
  );
}

const F = ({ label, children, span = 1 }) => (
  <label className={`block ${span === 2 ? "md:col-span-2" : ""} ${span === 3 ? "md:col-span-3" : ""}`}>
    <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1 block">{label}</span>
    {children}
  </label>
);
